import { FiActivity, FiBarChart2 } from 'react-icons/fi';

import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlayfulCardButton } from '@/components/custom/playful-card';

// Peças comuns da tela de alunos da trilha e da tela de trilhas de um aluno.

const STATUS_CONFIG = {
  active: { label: 'Em andamento', variant: 'warning' },
  paused: { label: 'Pausada', variant: 'neutral-lightest' },
  completed: { label: 'Concluída', variant: 'success' }
};

// Tema playful (professor): mesmo status com badge "soft" (fundo claro + texto colorido).
const PLAYFUL_STATUS_VARIANT = {
  active: 'warning-soft',
  paused: 'muted',
  completed: 'success-soft'
};

export function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

// Percentual como vem da API (ex.: 4.26 → "4,26%"), sem arredondar.
export function formatPercent(value) {
  return `${(value ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

// Linha principal em destaque + linha secundária (aluno: nome/e-mail; trilha: título/objetivo).
export function TitleCell({ title, subtitle, playful }) {
  if (playful) {
    return (
      <>
        <p
          className="mb-0 truncate text-sm font-bold"
          style={{ color: '#222731' }}
          title={title}
        >
          {title}
        </p>
        {subtitle && <p className="mb-0 mt-0.5 truncate text-sm text-neutral-400">{subtitle}</p>}
      </>
    );
  }

  return (
    <>
      <p className="mb-0 text-sm font-semibold text-primary-dark">{title}</p>
      {subtitle && <p className="mb-0 text-sm text-neutral">{subtitle}</p>}
    </>
  );
}

export function StatusBadge({ status, playful }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;

  if (playful) {
    return (
      <Badge
        variant={PLAYFUL_STATUS_VARIANT[status] || PLAYFUL_STATUS_VARIANT.active}
        size="sm"
        className="rounded-full text-sm"
      >
        {cfg.label}
      </Badge>
    );
  }

  return (
    <Badge
      size="sm"
      variant={cfg.variant}
    >
      {cfg.label}
    </Badge>
  );
}

// Ações de uma trilha do aluno (linha com id de UserLearningPath).
export function TrailActions({ playful, onProgress, onPerformance }) {
  if (playful) {
    return (
      <div className="flex flex-nowrap items-center justify-end gap-2">
        <PlayfulCardButton
          variant="outline"
          icon={FiActivity}
          className="h-7 w-auto px-4 text-sm"
          onClick={onProgress}
        >
          Progresso na Trilha
        </PlayfulCardButton>
        <PlayfulCardButton
          icon={FiBarChart2}
          className="-mt-[3px] h-7 w-auto px-4 text-sm"
          onClick={onPerformance}
        >
          Desempenho na Trilha
        </PlayfulCardButton>
      </div>
    );
  }

  return (
    <div className="flex flex-nowrap items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={onProgress}
      >
        <ButtonIcon>
          <FiActivity />
        </ButtonIcon>{' '}
        Progresso na Trilha
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={onPerformance}
      >
        <ButtonIcon>
          <FiBarChart2 />
        </ButtonIcon>{' '}
        Desempenho na Trilha
      </Button>
    </div>
  );
}

export function Pagination({ pagination, onChange, playful }) {
  if (pagination.totalPages <= 1) return null;

  return (
    <div className="mt-4 flex justify-center gap-2">
      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p =>
        playful ? (
          <PlayfulCardButton
            key={p}
            variant={p === pagination.currentPage ? 'continue' : 'outline'}
            className="h-8 w-8 flex-none px-0 text-sm"
            onClick={() => onChange(p)}
          >
            {p}
          </PlayfulCardButton>
        ) : (
          <Button
            key={p}
            size="xs"
            variant={p === pagination.currentPage ? 'default' : 'outline'}
            onClick={() => onChange(p)}
          >
            {p}
          </Button>
        )
      )}
    </div>
  );
}
